// Authentication utilities and guards

export const AuthGuard = {
  // Check if user has valid token (synchronous JWT validation)
  isAuthenticated() {
    const token = this.getToken();

    if (!token || token === '' || token === 'null' || token === 'undefined') {
      return false;
    }

    // Validate JWT token format and expiration
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        // Invalid JWT format
        this.clearAuth();
        return false;
      }

      // Decode the payload
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired
      if (payload.exp && payload.exp < currentTime) {
        console.log('Token has expired');
        this.clearAuth();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Invalid token format:', error);
      this.clearAuth();
      return false;
    }
  },

  // Get token from storage
  getToken() {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  },

  // Clear all auth data
  clearAuth() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userData');
    localStorage.removeItem('selectedOrganizationId');
    localStorage.removeItem('userOrganizations');
  },

  // Redirect to sign in
  redirectToSignIn(router) {
    if (router) {
      router.push('/sign-in');
    } else {
      window.location.href = '/sign-in';
    }
  }
};

// HTTP fetch wrapper with auth and error handling
export const authFetch = async (url, options = {}) => {
  // Check token validity before making request
  if (!AuthGuard.isAuthenticated()) {
    throw new Error('No valid authentication token');
  }

  const token = AuthGuard.getToken();
  
  // Add authorization header if token exists
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Check for 401 Unauthorized
    if (response.status === 401) {
      console.log('401 Unauthorized - clearing auth and redirecting to sign in');
      AuthGuard.clearAuth();
      AuthGuard.redirectToSignIn();
      throw new Error('Unauthorized - redirected to sign in');
    }

    return response;
  } catch (error) {
    // Network or other errors
    console.error('Auth fetch error:', error);
    throw error;
  }
};

// Routes that don't require authentication
export const publicRoutes = ['/', '/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/privacy', '/terms', '/logout', '/404', '/accept-invitation'];

// Check if route is public
export const isPublicRoute = (path) => {
  return publicRoutes.includes(path);
};

// Organization utilities
export const OrganizationManager = {
  // Get selected organization ID
  getSelectedOrganizationId() {
    return localStorage.getItem('selectedOrganizationId');
  },

  // Set selected organization ID
  setSelectedOrganizationId(orgId) {
    localStorage.setItem('selectedOrganizationId', orgId);
  },

  // Get user's organizations
  getUserOrganizations() {
    const orgsJson = localStorage.getItem('userOrganizations');
    return orgsJson ? JSON.parse(orgsJson) : [];
  },

  // Set user's organizations
  setUserOrganizations(organizations) {
    localStorage.setItem('userOrganizations', JSON.stringify(organizations));

    // Auto-select first organization if none selected
    if (!this.getSelectedOrganizationId() && organizations.length > 0) {
      this.setSelectedOrganizationId(organizations[0].id);
    }
  },

  // Fetch and update user's organizations from API
  async fetchUserOrganizations() {
    try {
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
      const response = await authFetch(`${apiBaseUrl}/api/organizations`);

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const organizations = await response.json();
      this.setUserOrganizations(organizations);
      return organizations;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      return [];
    }
  },

  // Build API URL with organization ID
  buildOrgUrl(endpoint) {
    const orgId = this.getSelectedOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
    return `${apiBaseUrl}/api/organizations/${orgId}${endpoint}`;
  },

  // Clear organization data
  clearOrganizationData() {
    localStorage.removeItem('selectedOrganizationId');
    localStorage.removeItem('userOrganizations');
  },

  // Get current organization role from JWT
  getOrganizationRole() {
    const token = AuthGuard.getToken();
    if (!token) {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));
      return payload.organizationRole || null;
    } catch (error) {
      console.error('Error getting organization role:', error);
      return null;
    }
  },

  // Check if current user can edit (is ADMIN or OPERATOR)
  canEdit() {
    const role = this.getOrganizationRole();
    return role === 'ADMIN' || role === 'OPERATOR';
  },

  // Check if current user is a VIEWER
  isViewer() {
    return this.getOrganizationRole() === 'VIEWER';
  },

  // Check if current user is an ADMIN
  isAdmin() {
    return this.getOrganizationRole() === 'ADMIN';
  },

  // Select an organization and get new JWT with organization context
  async selectOrganization(organizationId) {
    try {
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8080';
      const response = await authFetch(`${apiBaseUrl}/api/auth/select-organization/${organizationId}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to select organization');
      }

      const loginResponse = await response.json();

      // Update token with new organization-scoped JWT
      const tokenStorage = localStorage.getItem('token') ? localStorage : sessionStorage;
      tokenStorage.setItem('token', loginResponse.token);

      // Update selected organization ID
      this.setSelectedOrganizationId(organizationId);

      return loginResponse;
    } catch (error) {
      console.error('Error selecting organization:', error);
      throw error;
    }
  }
};